/**
 * EVD-177 — keeps Routine welcome gifts tied to their Routine line in the cart.
 *
 * Gift lines are added in the same /cart/add.js request as the Routine line
 * (assets/routine-plan-selector.js) with the hidden properties
 * _ed_gift = 'routine' and _ed_gift_for = <product id>. A gift is valid while
 * the cart still has a line of that product with a selling plan. There is no
 * token on the Routine line itself: quantity changes go through
 * theme.cart.changeItemWithProperties, which replaces line properties.
 *
 * On every cart:updated (cart drawer / cart page quantity change, multipack
 * conflict removal) and once on the cart page:
 *  - removes gifts whose Routine line is gone;
 *  - keeps one gift set per cart (duplicates removed, quantity capped at 1).
 * The checkout-side guard is the multipack app's Validation Function.
 */
(function () {
  'use strict';

  var running = false;
  // Line key + target quantity pairs whose update failed are not retried on
  // the next cart:updated, so an error can never turn into a request loop.
  var attempted = {};

  function isGift(item) {
    return !!(item.properties && item.properties._ed_gift);
  }

  function collectUpdates(cart) {
    var items = (cart && cart.items) || [];
    var updates = {};
    var seenVariants = {};

    items.forEach(function (item) {
      if (!isGift(item)) return;

      var forProduct = String(item.properties._ed_gift_for || '');
      var hasRoutine = items.some(function (other) {
        return !isGift(other)
          && String(other.product_id) === forProduct
          && !!other.selling_plan_allocation;
      });

      if (!hasRoutine || seenVariants[item.variant_id]) {
        updates[item.key] = 0;
      } else {
        seenVariants[item.variant_id] = true;
        if (item.quantity > 1) updates[item.key] = 1;
      }
    });

    return updates;
  }

  function getCart(detailCart) {
    if (detailCart && Array.isArray(detailCart.items)) return Promise.resolve(detailCart);
    return window.fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then(function (r) { return r.json(); });
  }

  function sync(detailCart) {
    if (running) return;
    running = true;

    getCart(detailCart)
      .then(function (cart) {
        var all = collectUpdates(cart);
        var updates = {};
        Object.keys(all).forEach(function (key) {
          var id = key + ':' + all[key];
          if (attempted[id]) return;
          attempted[id] = true;
          updates[key] = all[key];
        });
        if (!Object.keys(updates).length) return;

        // Same pattern as MultipackSelector#checkCartConflicts. The second
        // cart:updated finds nothing left to fix, so this does not loop.
        return window.fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('cart update ' + r.status);
            return r.json();
          })
          .then(function (updatedCart) {
            if (!updatedCart || !Array.isArray(updatedCart.items)) return;
            // Only what is still unfixed stays blocked: a gift re-added later
            // (same variant + properties = same line key) must be removable again.
            var stillWrong = collectUpdates(updatedCart);
            attempted = {};
            Object.keys(stillWrong).forEach(function (key) {
              attempted[key + ':' + stillWrong[key]] = true;
            });
            running = false;
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
            document.dispatchEvent(new CustomEvent('cart:build'));
          });
      })
      .catch(function () { /* silently fail, checkout validation still applies */ })
      .then(function () {
        running = false;
      });
  }

  document.addEventListener('cart:updated', function (e) {
    sync(e.detail && e.detail.cart);
  });

  function onReady() {
    if (document.body.classList.contains('template-cart')) sync(null);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
